import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { EventRouteBoundary } from "containers/event/EventRouteBoundary";
import { useGetEvent } from "hooks/event/useGetEvent";
import React, { useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from "react-router";
import { getEventDisplayInfo } from "utility";

import { ResaleApplication } from "./ResaleApplication";
import { ResaleApplicationCompletion } from "./ResaleApplicationCompletion";
import { ResaleApplicationConfirmation } from "./ResaleApplicationConfirmation";
import { useGetResaleRounds } from "./useGetResaleRounds";

import type { FC } from "react";

const RESALE_APPLICATION_PATH =
  "/events/:eventId/tickets/:ticketId/resale" as const;

/**
 * @todo
 * クレジットカード情報を取得するhooksを作成する。
 */
const DUMMY_CREDIT_CARD = {
  brand: "VISA",
  expirationDate: "12/28",
  lastFourDigits: "1234",
} as const;

export const ResaleApplicationRoutes: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { eventId, ticketId } = useParams<{
    eventId: string;
    ticketId: string;
  }>();

  const baseURL = `/events/${eventId}/tickets/${ticketId}/resale`;
  const quantitySelectionURL = `${baseURL}${location.search}`;
  const confirmationURL = `${baseURL}/confirm${location.search}`;
  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);

  /**
   * リセール関係のデータを取得する。
   */
  const { data, isLoading, isResaleEnabled } = useGetResaleRounds(eventId);
  const resaleApplicationData = data?.find(
    (application) => application.ticketFirestoreId === ticketId
  );

  /**
   * イベント関係のデータを取得する、
   */
  const { data: event, isLoading: isEventLoading } = useGetEvent(
    isResaleEnabled ? eventId : null
  );
  const displayEvent = event ? getEventDisplayInfo(event) : undefined;
  const eventOpenAt = event?.parts[0]?.openTime.toMillis();
  const eventStartAt = event?.parts[0]?.startTime.toMillis();

  if (!isResaleEnabled) {
    return <Redirect to="/" />;
  }

  if (isLoading || isEventLoading) {
    return <LoaderLayout />;
  }

  if (
    !resaleApplicationData ||
    resaleApplicationData.resaleApplicationStatus !== "accepting" ||
    Date.now() > resaleApplicationData.applicationEndAt ||
    !displayEvent ||
    eventOpenAt === undefined ||
    eventStartAt === undefined
  ) {
    return <Redirect to={eventId ? `/events/${eventId}` : "/"} />;
  }

  const eventRouteBoundaryProps = {
    event: displayEvent,
    pathname: location.pathname,
    redirectPath: `${location.pathname}${location.search}`,
  };

  // 受付不可の場合も`EventRouteBoundary`の内側でredirectする。
  if (
    !resaleApplicationData ||
    resaleApplicationData.resaleApplicationStatus !== "accepting" ||
    Date.now() > resaleApplicationData.applicationEndAt
  ) {
    return (
      <EventRouteBoundary {...eventRouteBoundaryProps}>
        <Redirect to={`/events/${eventId}`} />
      </EventRouteBoundary>
    );
  }

  const handleQuantityChange = (value: number | null) => {
    if (value !== null && value > resaleApplicationData.maxApplicableQuantity) {
      return;
    }

    setSelectedQuantity(value);
  };

  const handleGoNextSubmit = () => {
    history.push(confirmationURL);
  };

  return (
    <EventRouteBoundary {...eventRouteBoundaryProps}>
      <Switch>
        <Route exact path={`${RESALE_APPLICATION_PATH}/completed`}>
          {selectedQuantity === null ? (
            <Redirect to={quantitySelectionURL} />
          ) : (
            <ResaleApplicationCompletion
              creditCard={DUMMY_CREDIT_CARD}
              desiredQuantity={selectedQuantity}
              eventId={eventId}
              eventOpenAt={eventOpenAt}
              eventStartAt={eventStartAt}
              eventThumbnailUrl={displayEvent.thumbnail}
              eventTitle={displayEvent.eventTitle}
              lotteryDate={resaleApplicationData.lotteryDate}
              ticketName={resaleApplicationData.ticketName}
              ticketPriceAmount={resaleApplicationData.ticketPriceAmount}
            />
          )}
        </Route>

        <Route exact path={`${RESALE_APPLICATION_PATH}/confirm`}>
          {selectedQuantity === null ? (
            <Redirect to={quantitySelectionURL} />
          ) : (
            <ResaleApplicationConfirmation
              eventId={eventId}
              ticketId={ticketId}
              creditCard={DUMMY_CREDIT_CARD}
              desiredQuantity={selectedQuantity}
              eventOpenAt={eventOpenAt}
              eventStartAt={eventStartAt}
              eventTitle={displayEvent.eventTitle}
              purchaseFeeAmount={resaleApplicationData.purchaseFeeAmount}
              ticketName={resaleApplicationData.ticketName}
              ticketPriceAmount={resaleApplicationData.ticketPriceAmount}
            />
          )}
        </Route>

        <Route exact path={RESALE_APPLICATION_PATH}>
          <ResaleApplication
            eventId={eventId}
            eventOpenAt={eventOpenAt}
            eventStartAt={eventStartAt}
            eventThumbnailUrl={displayEvent.thumbnail}
            eventTitle={displayEvent.eventTitle}
            ticketName={resaleApplicationData.ticketName}
            ticketPriceAmount={resaleApplicationData.ticketPriceAmount}
            maxApplicableQuantity={resaleApplicationData.maxApplicableQuantity}
            applicationEndAt={resaleApplicationData.applicationEndAt}
            lotteryDate={resaleApplicationData.lotteryDate}
            roundNumber={resaleApplicationData.roundNumber}
            selectedQuantity={selectedQuantity}
            onQuantityChange={handleQuantityChange}
            onSubmit={handleGoNextSubmit}
          />
        </Route>
        <Redirect to={quantitySelectionURL} />
      </Switch>
    </EventRouteBoundary>
  );
};
